// Master Admin — Roles Catalog (Section 1 source of truth)
// Reads /api/admin/roles/catalog. Shows every canonical role, per-role
// user counts, curriculum mapping, and sample users (with public alias
// applied for master_admin).
//
// Notes:
//  • Defensive against missing fields — never crashes if a role has no
//    users / no permissions / no curriculum.
//  • Filter bar lets the admin focus on a single role family (admin,
//    affiliate, partner, ops) before any deploy verification.
//  • partner_affiliate is highlighted as DISTINCT from
//    third_party_partner_store / brand / company. These four roles must
//    never be merged.
import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Users, BookOpen, Shield, AlertTriangle, RefreshCw } from "lucide-react";
import api, { apiErrorMessage } from "@/lib/api";

const PARTNER_NOTE = {
  partner_affiliate:
    "Free affiliate manager / team lead. NOT a store. No location, no product upload.",
  third_party_partner_store:
    "Outside smoke shop / vendor. Uploads products, has a physical location, appears on the live map.",
  third_party_partner_brand:
    "Outside brand / glassblower / artist. Gets a dedicated shop section.",
  third_party_partner_company:
    "Outside corporate partner. May have multiple locations.",
};

const FAMILY = {
  admin:     ["master_admin", "admin", "management_admin", "secondary_admin", "partner_admin"],
  affiliate: ["partner_affiliate", "affiliate"],
  partner:   ["third_party_partner_store", "third_party_partner_brand", "third_party_partner_company", "lifted_partner"],
  ops:       ["driver", "franchisee", "trainer", "staff"],
  customer:  ["customer"],
};
const FILTERS = [
  { id: "all",       label: "All roles" },
  { id: "admin",     label: "Admin" },
  { id: "affiliate", label: "Affiliate" },
  { id: "partner",   label: "Third-Party Partners" },
  { id: "ops",       label: "Operations" },
  { id: "customer",  label: "Customer" },
];

export default function AdminRolesCatalog() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr("");
      try {
        const { data } = await api.get("/admin/roles/catalog");
        if (cancelled) return;
        setRows(Array.isArray(data?.roles) ? data.roles : []);
      } catch (e) {
        if (cancelled) return;
        setErr(apiErrorMessage(e) || "Could not load roles.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [reloadKey]);

  const visible = useMemo(() => {
    if (filter === "all") return rows;
    const allowed = new Set(FAMILY[filter] || []);
    return rows.filter((r) => allowed.has(r.id));
  }, [rows, filter]);

  const totals = useMemo(() => {
    const t = { roles: rows.length, users: 0 };
    rows.forEach((r) => { t.users += Number(r.user_count || 0); });
    return t;
  }, [rows]);

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6" data-testid="admin-roles-catalog">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div className="min-w-0">
          <div className="eyebrow text-lifted-dim">Master Admin · Section 1</div>
          <h1 className="font-anton text-4xl uppercase text-white">Roles Catalog</h1>
          <p className="text-lifted-dim text-sm mt-1 max-w-2xl">
            Single source of truth for every Lifted role. Partner Affiliate is
            NOT a Third-Party Partner Store / Brand / Company. Use this page
            to verify role separation before any deployment.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setReloadKey((k) => k + 1)}
          className="px-3 py-1.5 rounded-md bg-white/5 hover:bg-white/10 border border-white/10 text-xs text-white inline-flex items-center gap-1"
          data-testid="roles-catalog-refresh"
        >
          <RefreshCw size={12} /> Refresh
        </button>
      </header>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-2" data-testid="roles-filter-bar">
        {FILTERS.map((f) => (
          <button
            key={f.id}
            type="button"
            onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-full text-xs border transition ${
              filter === f.id
                ? "bg-lifted-green/20 border-lifted-green/60 text-lifted-green"
                : "bg-white/5 border-white/10 text-lifted-dim hover:text-white hover:bg-white/10"
            }`}
            data-testid={`roles-filter-${f.id}`}
          >
            {f.label}
          </button>
        ))}
        <div className="ml-auto text-[11px] text-lifted-dim font-mono self-center" data-testid="roles-catalog-summary">
          {totals.roles} roles · {totals.users} users
        </div>
      </div>

      {loading && (
        <div className="text-lifted-dim text-sm flex items-center gap-2" data-testid="roles-catalog-loading">
          <Loader2 size={14} className="animate-spin" /> Loading roles…
        </div>
      )}

      {err && !loading && (
        <div
          className="rounded-md border border-rose-500/40 bg-rose-500/10 text-rose-200 text-sm px-3 py-2 flex items-center gap-2"
          data-testid="roles-catalog-error"
        >
          <AlertTriangle size={14} /> {err}
        </div>
      )}

      {!loading && !err && visible.length === 0 && (
        <div className="text-lifted-dim text-sm" data-testid="roles-catalog-empty">
          No roles match this filter.
        </div>
      )}

      <div className="grid gap-3" data-testid="roles-list">
        {visible.map((r) => {
          const perms = Array.isArray(r.permissions) ? r.permissions : [];
          const modules = Array.isArray(r.curriculum_modules) ? r.curriculum_modules : [];
          const users = Array.isArray(r.users) ? r.users : [];
          const count = Number(r.user_count || 0);
          return (
            <section
              key={r.id}
              className="glass-panel p-5"
              data-testid={`role-${r.id}`}
            >
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.18em] font-bebas px-2 py-0.5 rounded-full bg-lifted-green/15 text-lifted-green border border-lifted-green/40 font-mono">
                      {r.id}
                    </span>
                    <h2 className="font-anton uppercase text-white text-xl">
                      {r.label || r.id}
                    </h2>
                    <span
                      className="text-[10px] text-lifted-dim font-mono inline-flex items-center gap-1"
                      data-testid={`role-count-${r.id}`}
                    >
                      <Users size={10} /> {count} users
                    </span>
                  </div>
                  {r.description && (
                    <p className="text-sm text-lifted-dim">{r.description}</p>
                  )}
                  {PARTNER_NOTE[r.id] && (
                    <p
                      className="text-xs text-amber-300 mt-2"
                      data-testid={`role-note-${r.id}`}
                    >
                      <AlertTriangle size={11} className="inline mr-1 -mt-0.5" />
                      {PARTNER_NOTE[r.id]}
                    </p>
                  )}
                </div>
              </div>

              <div className="grid sm:grid-cols-2 gap-3 mt-4">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bebas text-lifted-dim mb-1 inline-flex items-center gap-1">
                    <Shield size={10} /> Permissions
                  </div>
                  <ul className="text-xs text-white space-y-0.5">
                    {perms.length === 0 && (
                      <li className="text-lifted-dim font-mono">—</li>
                    )}
                    {perms.map((p) => (
                      <li key={p} className="font-mono">· {p}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bebas text-lifted-dim mb-1 inline-flex items-center gap-1">
                    <BookOpen size={10} /> Training modules
                  </div>
                  <div className="text-xs text-white font-mono">
                    {modules.length > 0 ? (
                      modules.join(" · ")
                    ) : (
                      <span className="text-lifted-dim">none assigned</span>
                    )}
                  </div>
                </div>
              </div>

              {users.length > 0 && (
                <div className="mt-4 pt-3 border-t border-white/5">
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bebas text-lifted-dim mb-1">
                    Sample users
                  </div>
                  <ul className="text-xs space-y-0.5">
                    {users.slice(0, 8).map((u) => (
                      <li key={u.id || u.email} className="text-white font-mono">
                        · {u.display_name || u.public_display_name || u.name || u.email}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </section>
          );
        })}
      </div>
    </div>
  );
}
